// Regras de editar e excluir a própria mensagem.
//
// Mora fora do ChatMessage/ChatContext por ser lógica pura: quem pode o quê e
// como a lista fica depois da mudança. O RLS é quem manda de verdade (policies
// de autor em chat_messages); isto aqui decide o que a tela oferece, para não
// apresentar um botão que o banco vai negar.

import type { ChatMessage } from '../../services/chatService';

// O texto ocupa o lugar da mensagem na conversa. A linha continua lá porque a
// FK de resposta é ON DELETE SET NULL: sumir com ela deixaria as respostas
// penduradas em lugar nenhum.
export const TEXTO_DE_MENSAGEM_APAGADA = 'Mensagem apagada';

export function foiApagada(mensagem: Pick<ChatMessage, 'deleted_at'>): boolean {
  return !!mensagem.deleted_at;
}

// `updated_at` não serve: nasce preenchido no envio. Só `edited_at` distingue
// uma mensagem reescrita de uma recém-enviada.
export function foiEditada(
  mensagem: Pick<ChatMessage, 'edited_at' | 'deleted_at'>
): boolean {
  return !!mensagem.edited_at && !foiApagada(mensagem);
}

function ehMinhaEViva(
  mensagem: Pick<ChatMessage, 'user_auth_id' | 'is_system_message' | 'deleted_at'>,
  meuAuthId: string | null
): boolean {
  if (!meuAuthId) return false;
  if (mensagem.is_system_message) return false;
  if (foiApagada(mensagem)) return false;
  return mensagem.user_auth_id === meuAuthId;
}

// Editar mexe no texto. Numa mensagem que é só anexo (a de voz vai sem texto de
// propósito), editar é escrever a legenda que não existia — vale a pena
// oferecer, e é a mesma operação.
export const podeEditar = ehMinhaEViva;

export const podeExcluir = ehMinhaEViva;

// A tela envia o texto já aparado; vazio significa "apagar", que é outra ação e
// tem outra confirmação. Uma edição que não muda nada não precisa ir ao banco.
export function edicaoVaiAoBanco(textoAtual: string, textoNovo: string): boolean {
  const novo = textoNovo.trim();
  if (!novo) return false;
  return novo !== textoAtual.trim();
}

// Troca na lista a mensagem que mudou, preservando o que o servidor não
// devolve no UPDATE: autor, anexos, reações e a mensagem citada vêm de outras
// consultas e sumiriam da tela se a linha fosse substituída inteira.
//
// Devolve a MESMA lista quando não há o que trocar, para o React não
// re-renderizar a sala inteira a cada evento de Realtime de outra sala.
export function aplicarMensagemAtualizada(
  mensagens: ChatMessage[],
  atualizada: Pick<ChatMessage, 'id'> & Partial<ChatMessage>
): ChatMessage[] {
  const indice = mensagens.findIndex(m => m.id === atualizada.id);
  if (indice === -1) return mensagens;

  const anterior = mensagens[indice];
  const nova: ChatMessage = {
    ...anterior,
    ...atualizada,
    user: atualizada.user ?? anterior.user,
    reply_to_message: atualizada.reply_to_message ?? anterior.reply_to_message,
    attachments: atualizada.attachments ?? anterior.attachments,
    reactions: atualizada.reactions ?? anterior.reactions,
  };

  // Mensagem apagada perde os anexos: os arquivos saem do Storage junto.
  if (foiApagada(nova)) {
    nova.attachments = [];
    nova.content = '';
  }

  const copia = [...mensagens];
  copia[indice] = nova;
  return copia;
}
